/*
 * -----------------------------------------------------------------------
 *  <copyright file="horizontal-bar-chart.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */
import React, { useState, useEffect, useRef, useContext } from 'react';
import Highcharts from 'highcharts/esm/highcharts.js';
import 'highcharts/modules/exporting';
import 'highcharts/modules/offline-exporting';
import HighchartsReact from 'highcharts-react-official';
import { getChartEvents } from '@components-clean/core/charts/shared/utils';
import {
    HC_COLORS_STANDARD,
    IOpraChartKey,
    IOpraChartVerticalLine,
    IOpraColumnSeries,
    OpraColours,
    OpraVerticalAlign,
} from 'interfaces/opra-chart.interface';
import Loading from 'views/components/core/loading';
import { PanelFrameContext } from 'views/components/core/panel-frame';

export interface IOpraHorizontalBarChart {
    plotTitle: string;
    categories: string[];
    xAxisTitle: string;
    series: IOpraColumnSeries[];
    verticalScroll?: boolean;
    verticalLines?: IOpraChartVerticalLine[];
    getRef?: Function;
    secondaryKey?: IOpraChartKey;
    showLegend?: boolean;
    formatTooltip?: Function;
    maxValue?: number;
    hidePlotTitle?: boolean;
    displayFullScreenAndPrintTitle?: boolean;
}

const HorizontalBarChart = (chartProps: IOpraHorizontalBarChart) => {
    const {
        plotTitle,
        xAxisTitle,
        series,
        getRef,
        showLegend,
        secondaryKey,
        maxValue,
        formatTooltip,
        verticalScroll,
        categories,
        hidePlotTitle,
        displayFullScreenAndPrintTitle = false,
    } = chartProps;
    const [options, setOptions] = useState<HighchartsReact.Props | null>(null);
    const charts = useRef<HighchartsReact.RefObject>(null);
    const height = useContext(PanelFrameContext);
    const [style, setStyle] = useState({
        style: {
            height: '350px',
        },
    });

    useEffect(() => {
        if (height?.height && height?.height !== style.style.height)
            setStyle({
                style: {
                    height: height?.height,
                },
            });
    }, [height?.height]);

    useEffect(() => {
        if (getRef && charts) getRef(charts?.current);
    }, [charts.current, options]);

    const max = Math.max(
        ...categories.map((cat: string, idx: number) =>
            series.reduce((tot: number, cur): number => Math.round(tot + cur.values[idx]), 0),
        ),
    );

    useEffect(
        () =>
            setOptions({
                credits: {
                    enabled: false,
                },
                chart: {
                    type: 'bar',
                    zoomType: verticalScroll ? 'x' : 'xy',
                    marginLeft: 200,
                    style: {
                        fontSize: '1.2rem',
                    },
                    events: displayFullScreenAndPrintTitle
                        ? getChartEvents(false, false, plotTitle, undefined, {
                              fullScreenOptions: {
                                  title: { text: plotTitle, style: { fontWeight: '600' } },
                              },
                              printOptions: {
                                  title: { text: plotTitle, style: { fontWeight: '500' } },
                              },
                          })
                        : undefined,
                },
                exporting: {
                    chartOptions: { title: { text: plotTitle, style: { fontWeight: 'semi-bold' } } },
                    enabled: !getRef,
                    fallbackToExportServer: false,
                    buttons: {
                        contextButton: {
                            enabled: false,
                        },
                    },
                },
                title: hidePlotTitle ? {} : { text: plotTitle },
                tooltip: formatTooltip
                    ? {
                          formatter() {
                              const self: any = this;
                              return formatTooltip(
                                  self.key,
                                  self.percentage,
                                  self.series.name,
                                  self.series.options.custom,
                              );
                          },
                      }
                    : {
                          formatter: undefined,
                      },
                subtitle:
                    secondaryKey === undefined
                        ? undefined
                        : {
                              text: secondaryKey.text,
                              useHTML: true,
                              verticalAlign:
                                  secondaryKey.position === OpraVerticalAlign.top
                                      ? 'top'
                                      : secondaryKey.position === OpraVerticalAlign.middle
                                        ? 'middle'
                                        : 'bottom',
                              style: { display: (showLegend ?? true) ? undefined : 'none' },
                          },
                legend: {
                    enabled: showLegend ?? true,
                    itemStyle: {
                        color: HC_COLORS_STANDARD[OpraColours.DarkGrey],
                        cursor: 'pointer',
                        fontWeight: 'normal',
                        fontSize: '12px',
                        textOverflow: 'ellipsis',
                    },
                },
                xAxis: {
                    type: 'category',
                    title: {
                        text: null,
                    },
                    scrollbar: {
                        enabled: verticalScroll,
                    },
                    min: 0,
                },
                yAxis: {
                    title: {
                        text: xAxisTitle,
                    },
                    allowDecimals: false,
                    min: 0,
                    max: maxValue ? (max > maxValue ? maxValue : max) : max,
                },
                plotOptions: {
                    bar: {
                        stacking: 'normal',
                    },
                },
                series: series.map((s) => ({
                    name: s.name,
                    color: s.color,
                    data: categories.map((cat, idx) => [cat, s.values[idx]]),
                    point: s.onClick ? { events: { click: s.onClick } } : undefined,
                    cursor: s.onClick ? 'pointer' : undefined,
                    custom: s.custom,
                })),
            }),
        [chartProps],
    );

    if (options === null) return <Loading />;
    return (
        <HighchartsReact
            ref={charts}
            highcharts={Highcharts}
            constructorType="chart"
            options={options}
            containerProps={style} // Mending size after fullscreen and setting with for expand chart functionality
        />
    );
};

export default HorizontalBarChart;
